import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setToken, setUser } from '../../lib/auth.js';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  UserIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { ShieldCheckIcon as ShieldCheckIconSolid } from '@heroicons/react/24/solid';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Card from '../../components/ui/Card.jsx';
// import MaintenanceBanner from '../../components/common/MaintenanceBanner.jsx';
import LanguageSwitcher from '../../components/common/LanguageSwitcher.jsx';
import Logo from '../../assets/srpf_new_logo.jpeg';

// Modern Visual Branding Area Component for Left Side (with Logo)
const VisualBrandingArea = ({ currentLang, className = '' }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Taglines for Marathi and Hindi (English uses hardcoded bullet points)
  const taglines = {
    mr: {
      heading: 'सुरक्षित कमांडंट डॅशबोर्ड',
      description: 'SRPF कर्मचाऱ्यांसाठी कार्यक्षम व्यवस्थापन आणि निर्बाध कार्यांसाठी सक्षम करणे.'
    },
    hi: {
      heading: 'सुरक्षित कमांडेंट डैशबोर्ड',
      description: 'SRPF कर्मियों के लिए कुशल प्रबंधन और निर्बाध संचालन को सक्षम करना।'
    }
  };

  const currentTagline = taglines[currentLang];
  const isEnglish = currentLang === 'en';

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <div className={`relative h-full w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden ${className}`} style={{ position: 'relative', zIndex: 1 }}>
      {/* Modern Animated Background */}
      <div className="absolute inset-0 overflow-hidden" style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Content Display - Centered */}
      <div className="relative h-full w-full overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
        <div className="relative w-full h-full flex flex-col items-center justify-center px-2 min-[375px]:px-3 sm:px-4 md:px-6 lg:px-8 py-2 min-[375px]:py-3 sm:py-4 md:py-6 lg:py-8">
          {/* Logo Container */}
          <div className="relative flex justify-center mb-0 px-1 min-[375px]:px-2 sm:px-3 md:px-4">
            <div className="relative rounded-lg min-[375px]:rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden border-2 min-[375px]:border-2 sm:border-3 md:border-4 border-white/40 bg-white/95 backdrop-blur-sm shadow-2xl 
              w-[100px] min-[375px]:w-[120px] sm:w-[140px] md:w-[160px] lg:w-[200px] xl:w-[260px] 2xl:w-[320px]
              flex items-center justify-center
              p-2 min-[375px]:p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 2xl:p-10
              transform transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.4)]"
              style={{ boxShadow: '0 0 30px rgba(255, 255, 255, 0.2), 0 0 60px rgba(20, 184, 166, 0.1)' }}>
              {!imageError ? (
                <img
                  src={Logo}
                  alt="SRPF Command Center - Service, Readiness, Public Faith"
                  className={`w-full h-auto transition-opacity duration-500 ${
                    imageLoading ? 'opacity-0' : 'opacity-100'
                  }`}
                  style={{ 
                    display: 'block', 
                    width: '100%',
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  loading="eager"
                />
              ) : (
                <div className="w-full h-48 sm:h-56 md:h-64 lg:h-72 xl:h-80 bg-white/10 flex items-center justify-center rounded-lg">
                  <ShieldCheckIcon className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 text-white/80" />
                </div>
              )}

              {/* Loading Skeleton */}
              {imageLoading && (
                <div className="absolute inset-0 bg-white/10 animate-pulse flex items-center justify-center rounded-lg">
                  <div className="w-16 h-16 sm:w-20 sm:w-20 md:w-24 md:h-24 lg:w-28 lg:h-28 bg-white/20 rounded-lg"></div>
                </div>
              )}
            </div>
          </div>

          {/* Tagline - Below Logo (All Languages) */}
          <div className="px-1 min-[375px]:px-2 sm:px-3 md:px-4 mt-1.5 min-[375px]:mt-2 sm:mt-3 md:mt-4 text-center w-full">
            {isEnglish ? (
              // English: Show bullet points (left-aligned within centered block)
              <ul className="space-y-1 min-[375px]:space-y-1.5 sm:space-y-2 md:space-y-2.5 lg:space-y-3 list-none inline-block text-left">
                <li className="flex items-start group">
                  <span className="text-teal-400 mr-2 min-[375px]:mr-2.5 sm:mr-3 md:mr-3.5 lg:mr-4 mt-0.5 min-[375px]:mt-0.5 sm:mt-1 text-sm min-[375px]:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl transition-transform group-hover:scale-125">•</span>
                  <span className="text-white text-[11px] min-[375px]:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-semibold break-words group-hover:text-teal-200 transition-colors">
                    Centralized communication
                  </span>
                </li>
                <li className="flex items-start group">
                  <span className="text-teal-400 mr-2 min-[375px]:mr-2.5 sm:mr-3 md:mr-3.5 lg:mr-4 mt-0.5 min-[375px]:mt-0.5 sm:mt-1 text-sm min-[375px]:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl transition-transform group-hover:scale-125">•</span>
                  <span className="text-white text-[11px] min-[375px]:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-semibold break-words group-hover:text-teal-200 transition-colors">
                    Real-time tracking
                  </span>
                </li>
                <li className="flex items-start group">
                  <span className="text-teal-400 mr-2 min-[375px]:mr-2.5 sm:mr-3 md:mr-3.5 lg:mr-4 mt-0.5 min-[375px]:mt-0.5 sm:mt-1 text-sm min-[375px]:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl transition-transform group-hover:scale-125">•</span>
                  <span className="text-white text-[11px] min-[375px]:text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 2xl:text-2xl font-semibold break-words group-hover:text-teal-200 transition-colors">
                    Faster decisions
                  </span>
                </li>
              </ul>
            ) : (
              // Marathi/Hindi: Show heading and description
              <>
                <h2 className="text-xs min-[375px]:text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold text-white mb-1 min-[375px]:mb-1.5 sm:mb-2 md:mb-2.5 lg:mb-3 break-words">
                  {currentTagline.heading}
                </h2>
                <p className="text-white/80 text-[9px] min-[375px]:text-[10px] sm:text-xs md:text-sm lg:text-base leading-relaxed break-words px-0.5 min-[375px]:px-1">
                  {currentTagline.description}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Form Component - defined outside to avoid closure issues
const LoginForm = ({ 
  formIdPrefix = '',
  formData,
  handleInputChange,
  handleSubmit,
  errors,
  showPassword,
  setShowPassword,
  capsLockOn,
  isLoading,
  handleForgotPassword,
  handlePrivacyPolicy
}) => {
  return (
    <div className="w-full max-w-md mx-auto px-3 min-[375px]:px-4 sm:px-5 md:px-6">
      {/* Logo and Header */}
      <div className="text-center mb-3 min-[375px]:mb-4 sm:mb-5 md:mb-6">
        <div className="mx-auto w-12 h-12 min-[375px]:w-14 min-[375px]:h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-20 lg:h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg min-[375px]:rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 min-[375px]:mb-3 sm:mb-4 shadow-lg shadow-teal-500/30 transform transition-all duration-300 hover:scale-110 hover:shadow-xl hover:shadow-teal-500/40">
          <ShieldCheckIconSolid className="w-6 h-6 min-[375px]:w-7 min-[375px]:h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 text-white" />
        </div>
        
        <h1 className="text-lg min-[375px]:text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-1 min-[375px]:mb-1.5 sm:mb-2">
          Sign In
        </h1>
        
        <p className="text-[11px] min-[375px]:text-xs sm:text-sm text-gray-500 px-1 min-[375px]:px-2">
          Welcome back. Enter your credentials to continue.
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-2.5 min-[375px]:space-y-3 sm:space-y-4">
        {/* General Error */}
        {errors.general && (
          <div 
            className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md animate-slide-down"
            aria-live="polite"
            aria-atomic="true"
          >
            <p className="text-xs sm:text-sm font-medium text-red-700">{errors.general}</p>
          </div>
        )}

        {/* Email Input */}
        <div className="space-y-1">
          <label htmlFor={`email-${formIdPrefix}`} className="block text-[11px] min-[375px]:text-xs sm:text-sm font-semibold text-gray-700 mb-0.5 min-[375px]:mb-1">
            Email Address
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 min-[375px]:pl-3.5 sm:pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-teal-600">
              <UserIcon className="h-3.5 w-3.5 min-[375px]:h-4 min-[375px]:w-4 sm:h-5 sm:w-5 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
            </div>
            <input
              id={`email-${formIdPrefix}`}
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full pl-9 min-[375px]:pl-10 sm:pl-12 pr-3 min-[375px]:pr-3.5 sm:pr-4 py-2 min-[375px]:py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg sm:rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all duration-300 shadow-sm hover:border-gray-300 hover:shadow-md touch-manipulation"
              placeholder="Enter your email address"
              autoComplete="email"
              aria-describedby={errors.email ? `email-error-${formIdPrefix}` : undefined}
            />
          </div>
          {errors.email && (
            <p id={`email-error-${formIdPrefix}`} className="text-[10px] min-[375px]:text-xs text-red-600 mt-0.5 min-[375px]:mt-1 flex items-center gap-1 animate-slide-down">
              <ExclamationTriangleIcon className="h-2.5 w-2.5 min-[375px]:h-3 min-[375px]:w-3 flex-shrink-0" />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        {/* Password Input */}
        <div className="space-y-1">
          <label htmlFor={`password-${formIdPrefix}`} className="block text-[11px] min-[375px]:text-xs sm:text-sm font-semibold text-gray-700 mb-0.5 min-[375px]:mb-1">
            Password
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 min-[375px]:pl-3.5 sm:pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-teal-600">
              <LockClosedIcon className="h-3.5 w-3.5 min-[375px]:h-4 min-[375px]:w-4 sm:h-5 sm:w-5 text-gray-400 group-focus-within:text-teal-600 transition-colors" />
            </div>
            <input
              id={`password-${formIdPrefix}`}
              type={showPassword ? 'text' : 'password'}
              value={formData.password || ''}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full pl-9 min-[375px]:pl-10 sm:pl-12 pr-9 min-[375px]:pr-10 sm:pr-12 py-2 min-[375px]:py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-lg sm:rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all duration-300 shadow-sm hover:border-gray-300 hover:shadow-md touch-manipulation"
              placeholder="Enter your password"
              autoComplete="current-password"
              aria-describedby={errors.password ? `password-error-${formIdPrefix}` : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 min-[375px]:pr-3.5 sm:pr-4 flex items-center text-gray-400 hover:text-teal-600 transition-colors duration-200 touch-manipulation min-w-[36px] min-[375px]:min-w-[44px]"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-3.5 w-3.5 min-[375px]:h-4 min-[375px]:w-4 sm:h-5 sm:w-5" />
              ) : (
                <EyeIcon className="h-3.5 w-3.5 min-[375px]:h-4 min-[375px]:w-4 sm:h-5 sm:w-5" />
              )}
            </button>
          </div>
          
          {/* Caps Lock Warning */}
          {capsLockOn && (
            <p className="text-[10px] min-[375px]:text-xs text-amber-600 flex items-center gap-1 mt-0.5 min-[375px]:mt-1 animate-slide-down">
              <ExclamationTriangleIcon className="h-2.5 w-2.5 min-[375px]:h-3 min-[375px]:w-3 flex-shrink-0" />
              <span>Caps Lock is on</span>
            </p>
          )}
          
          {errors.password && (
            <p id={`password-error-${formIdPrefix}`} className="text-[10px] min-[375px]:text-xs text-red-600 mt-0.5 min-[375px]:mt-1 flex items-center gap-1 animate-slide-down">
              <ExclamationTriangleIcon className="h-2.5 w-2.5 min-[375px]:h-3 min-[375px]:w-3 flex-shrink-0" />
              <span>{errors.password}</span>
            </p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex flex-col min-[375px]:flex-row items-start min-[375px]:items-center justify-between gap-1.5 min-[375px]:gap-2 sm:gap-0 text-[11px] min-[375px]:text-xs sm:text-sm">
          <label className="flex items-center cursor-pointer group touch-manipulation">
            <input
              type="checkbox"
              className="rounded border-2 border-gray-300 text-teal-600 focus:ring-2 focus:ring-teal-500/50 focus:ring-offset-0 w-3.5 h-3.5 min-[375px]:w-4 min-[375px]:h-4 transition-all cursor-pointer"
            />
            <span className="ml-1.5 min-[375px]:ml-2 sm:ml-2.5 text-gray-700 group-hover:text-gray-900 transition-colors">Remember me</span>
          </label>
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="font-semibold text-teal-600 hover:text-teal-700 transition-colors duration-200 hover:underline touch-manipulation text-left min-[375px]:text-right"
          >
            Forgot password?
          </button>
        </div>

        {/* Sign In Button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isLoading}
          className="w-full h-9 min-[375px]:h-10 sm:h-11 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold text-xs min-[375px]:text-sm sm:text-base rounded-lg sm:rounded-xl shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 touch-manipulation"
          disabled={isLoading}
        >
          Sign In
        </Button>

        {/* Compliance Notice */}
        <div className="pt-0.5 min-[375px]:pt-1">
          <p className="text-[9px] min-[375px]:text-[10px] sm:text-xs text-gray-500 text-center leading-tight px-1 min-[375px]:px-2">
            Use of this system is monitored. Unauthorized access is prohibited.
          </p>
        </div>

        {/* Privacy Policy Button */}
        <div className="pt-0.5 min-[375px]:pt-1">
          <button
            type="button"
            onClick={handlePrivacyPolicy}
            className="w-full inline-flex items-center justify-center gap-1 min-[375px]:gap-1.5 text-[10px] min-[375px]:text-xs sm:text-sm font-medium text-gray-600 hover:text-teal-600 transition-all duration-200 px-2 min-[375px]:px-3 sm:px-4 py-1 min-[375px]:py-1.5 sm:py-2 rounded-lg sm:rounded-xl hover:bg-gray-50 hover:shadow-sm group touch-manipulation"
          >
            <DocumentTextIcon className="h-2.5 w-2.5 min-[375px]:h-3 min-[375px]:w-3 sm:h-4 sm:w-4 group-hover:scale-110 transition-transform flex-shrink-0" />
            <span className="text-center">Privacy Policy & Security Measures</span>
          </button>
        </div>
      </form>
    </div>
  );
};

const SRPFLoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const navigate = useNavigate();

  // Load language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('srpf-language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Caps Lock detection
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e instanceof KeyboardEvent && e.getModifierState && e.getModifierState('CapsLock')) {
        setCapsLockOn(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e instanceof KeyboardEvent && e.getModifierState && !e.getModifierState('CapsLock')) {
        setCapsLockOn(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.includes('@') || formData.email.length < 5) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      // Make API call to login endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Login successful - store only token for security reasons
        const tokenStored = setToken(data.token);
        // Skip storing user data in localStorage for security
        
        console.log('✅ Login successful!');
        console.log('📝 Token stored:', tokenStored ? 'YES' : 'NO');
        console.log('🔒 User data not stored locally for security');
        
        // Simple, direct redirect to dashboard
        console.log('🔄 Redirecting to dashboard...');
        navigate('/dashboard');
        
      } else {
        // Login failed - show error message
        setErrors({
          general: data.message || 'Login failed. Please check your credentials.'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language.code);
    // Save language preference to localStorage
    localStorage.setItem('srpf-language', language.code);
  };

  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
  };

  const handleContactIT = () => {
    console.log('Contact IT Service Desk clicked');
  };

  const handlePrivacyPolicy = () => {
    console.log('Privacy Policy clicked');
    navigate('/privacy-policy');
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      {/* Header with Language Switcher - Modern Gradient Background */}
      <header className="flex-shrink-0 flex justify-between items-center px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5 bg-gradient-to-r from-teal-700 via-teal-600 to-teal-700 shadow-lg relative z-50" style={{ position: 'relative', zIndex: 50 }}>
        <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
          <div className="p-1 sm:p-1.5 bg-white/10 rounded-lg backdrop-blur-sm flex-shrink-0">
            <BuildingOfficeIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
          </div>
          <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-white tracking-wide truncate">
            STATE RESERVE POLICE FORCE
          </span>
        </div>
        <div className="flex-shrink-0 ml-2 sm:ml-4">
          <LanguageSwitcher onLanguageChange={handleLanguageChange} variant="dark" />
        </div>
      </header>

      {/* Main Content - Split Screen Layout */}
      <main className="flex-1 flex overflow-hidden min-h-0" style={{ position: 'relative', zIndex: 1 }}>
        {/* Desktop Layout: 50/50 Split - Large screens (1024px+) */}
        <div className="hidden lg:flex w-full h-full">
          {/* LEFT: Visual Branding Area with Logo (50%) - Dark Gradient */}
          <div className="w-1/2 h-full overflow-hidden" style={{ position: 'relative', zIndex: 1 }}>
            <VisualBrandingArea 
              currentLang={currentLanguage}
              className="h-full"
            />
          </div>

          {/* RIGHT: Login Form (50%) - Modern White Background with Subtle Pattern */}
          <div className="w-1/2 bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 2xl:p-12 overflow-hidden relative">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-[0.02]" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgb(0,0,0) 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}></div>
            <div className="relative z-10 w-full max-w-md xl:max-w-lg">
            <LoginForm 
              formIdPrefix="desktop"
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              errors={errors}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              capsLockOn={capsLockOn}
              isLoading={isLoading}
              handleForgotPassword={handleForgotPassword}
              handlePrivacyPolicy={handlePrivacyPolicy}
            />
            </div>
          </div>
        </div>

        {/* Tablet Layout: Stacked (768px - 1023px) */}
        <div className="hidden md:block lg:hidden w-full h-full flex flex-col overflow-hidden">
          {/* Visual Area with Logo - Top */}
          <div className="flex-shrink-0 h-36 sm:h-40 md:h-44 lg:h-48">
            <VisualBrandingArea 
              currentLang={currentLanguage}
              className="h-full"
            />
          </div>
          
          {/* Login Form - Bottom */}
          <div className="flex-1 bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-5 md:p-6 lg:p-8 overflow-hidden min-h-0">
            <LoginForm 
              formIdPrefix="tablet"
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              errors={errors}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              capsLockOn={capsLockOn}
              isLoading={isLoading}
              handleForgotPassword={handleForgotPassword}
              handlePrivacyPolicy={handlePrivacyPolicy}
            />
          </div>
        </div>

        {/* Mobile Layout: Stacked (< 768px) */}
        <div className="md:hidden w-full h-full flex flex-col overflow-hidden">
          {/* Visual Area with Logo - Top (Smaller) */}
          <div className="flex-shrink-0 h-28 min-[375px]:h-32 sm:h-36 md:h-40">
            <VisualBrandingArea 
              currentLang={currentLanguage}
              className="h-full"
            />
          </div>
          
          {/* Login Form - Bottom */}
          <div className="flex-1 bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-2.5 min-[375px]:p-3 sm:p-4 overflow-hidden min-h-0">
            <LoginForm 
              formIdPrefix="mobile"
              formData={formData}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              errors={errors}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              capsLockOn={capsLockOn}
              isLoading={isLoading}
              handleForgotPassword={handleForgotPassword}
              handlePrivacyPolicy={handlePrivacyPolicy}
            />
          </div>
        </div>
      </main>

      {/* Footer - Modern Footer */}
      <footer className="flex-shrink-0 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200/50 text-center py-1.5 min-[375px]:py-2 sm:py-3 md:py-4 px-2 min-[375px]:px-3 sm:px-4 shadow-sm">
        <div className="max-w-[1280px] mx-auto">
          {/* Copyright and Powered by - Responsive Layout */}
          <div className="flex flex-col min-[375px]:flex-row items-center justify-center gap-0.5 min-[375px]:gap-1 sm:gap-2 md:gap-3 text-[9px] min-[375px]:text-[10px] sm:text-xs md:text-sm text-gray-600">
            <span className="font-medium">© 2024 SRPF Command Dashboard. All rights reserved.</span>
            <span className="hidden sm:inline text-gray-400">|</span>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Powered by </span>
              <a 
                href="https://navantra.in/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-600 font-semibold hover:text-teal-700 hover:underline transition-all duration-200 break-words"
              >
                Navantra Global Solutions Pvt. Ltd
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SRPFLoginPage;
